import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import SavedQuotes from '../components/SavedQuotes';
import { useQuotes } from '../context/QuotesContext';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';
import { Button } from '../components/ui/button';

const applyQuoteToSession = (quote, loadBackendQuoteSession, setIncludeTemplateInExport) => {
  loadBackendQuoteSession(quote);
  const storedTemplate = quote?.quote_data?.exportOptions?.includeTemplateInExport;
  if (typeof storedTemplate !== 'boolean') {
    setIncludeTemplateInExport(false);
  }
};

const DashboardQuoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { loadBackendQuoteSession, setIncludeTemplateInExport } = useQuotes();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    if (!id) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setSessionReady(false);

      const quoteFromState = location.state?.quote;
      if (quoteFromState && String(quoteFromState.id) === String(id)) {
        if (cancelled) return;
        applyQuoteToSession(quoteFromState, loadBackendQuoteSession, setIncludeTemplateInExport);
        setSessionReady(true);
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/quotes/${id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        });
        if (cancelled) return;
        applyQuoteToSession(response.data, loadBackendQuoteSession, setIncludeTemplateInExport);
        setSessionReady(true);
      } catch (err) {
        if (cancelled) return;
        if (quoteFromState) {
          applyQuoteToSession(quoteFromState, loadBackendQuoteSession, setIncludeTemplateInExport);
          setSessionReady(true);
          return;
        }
        const detail = err.response?.data?.detail;
        setError(
          typeof detail === 'string'
            ? detail
            : 'Failed to load quote. Restart the backend server if this is a new install.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    id,
    authLoading,
    isAuthenticated,
    navigate,
    loadBackendQuoteSession,
    setIncludeTemplateInExport,
  ]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center text-gray-600">
        Loading quote…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center gap-4 p-6">
        <p className="text-red-600 text-center max-w-md">{error}</p>
        <Button variant="outline" onClick={() => navigate('/dashboard?tab=quotes')}>
          Back to quotes
        </Button>
      </div>
    );
  }

  if (!sessionReady) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center gap-4 p-6">
        <p className="text-gray-600">No quote data available.</p>
        <Button variant="outline" onClick={() => navigate('/dashboard?tab=quotes')}>
          Back to quotes
        </Button>
      </div>
    );
  }

  return (
    <SavedQuotes
      asPage
      dashboardMode
      forceEditing
      returnPath="/dashboard?tab=quotes"
    />
  );
};

export default DashboardQuoteDetail;
